import axios from "axios";
import {
  ShieldCheck, SealCheck, Leaf, GraduationCap, Broom, Fire, Medal, Crown, Diamond, Hexagon,
  Shield, Star, Lightning, Footprints, Crosshair, Eye, ClipboardText, Wrench, Target,
  Lightbulb, FireSimple, Trophy, SunHorizon, MoonStars, CalendarCheck, Compass, Sword, Gift,
} from "@phosphor-icons/react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/gamification`;
export const get = (path) => axios.get(`${API}${path}`, { withCredentials: true }).then((r) => r.data);

export const LEVELS = {
  "Bronce": { color: "#C97B48", glow: "rgba(201,123,72,.35)", icon: Shield },
  "Plata": { color: "#C0C8D4", glow: "rgba(192,200,212,.35)", icon: Shield },
  "Oro": { color: "#F5B83D", glow: "rgba(245,184,61,.4)", icon: Medal },
  "Platino": { color: "#7FE3D8", glow: "rgba(127,227,216,.35)", icon: Hexagon },
  "Diamante": { color: "#6CB8FF", glow: "rgba(108,184,255,.4)", icon: Diamond },
  "Maestro HSE": { color: "#FF4D57", glow: "rgba(255,77,87,.45)", icon: Crown },
  "Leyenda HSE": { color: "#FFD86B", glow: "rgba(255,216,107,.55)", icon: Trophy },
};
export const levelOf = (name) => LEVELS[name] || LEVELS.Bronce;

export const CATS = {
  seguridad: { label: "Seguridad", color: "#E31E24", icon: ShieldCheck },
  calidad: { label: "Calidad", color: "#6CB8FF", icon: SealCheck },
  ambiente: { label: "Medio Ambiente", color: "#3DDC84", icon: Leaf },
  capacitacion: { label: "Capacitación", color: "#B98BFF", icon: GraduationCap },
  "5s": { label: "5S", color: "#F5B83D", icon: Broom },
  bonus: { label: "Rachas", color: "#FF8A3D", icon: Fire },
};

export const BADGE_ICONS = {
  Footprints, Crosshair, Eye, ClipboardText, Wrench, GraduationCap, Target, Leaf, Broom,
  Lightbulb, Fire, FireSimple, Crown, SunHorizon, MoonStars, CalendarCheck, Compass, Lightning, Sword, Gift,
};

export const PERIODS = [["semana", "Semanal"], ["mes", "Mensual"], ["anio", "Anual"], ["historico", "Histórico"]];

export const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
export const fmtMonth = (ym) => (ym ? `${MONTHS[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(2, 4)}` : "");

export function LevelEmblem({ name, size = 56 }) {
  const L = levelOf(name);
  const Icon = L.icon;
  return (
    <div className="relative grid place-items-center rounded-full shrink-0"
         style={{ width: size, height: size, background: `radial-gradient(circle at 30% 25%, ${L.color}55, #0B0D10 70%)`,
                  boxShadow: `0 0 ${size / 2}px ${L.glow}, inset 0 0 0 2px ${L.color}` }}
         data-testid="level-emblem">
      <Icon size={size * 0.5} weight="fill" style={{ color: L.color }} />
    </div>
  );
}

export function LevelPips({ stars, total = 7, size = 8 }) {
  return (
    <span className="inline-flex items-center gap-1">
      {Array.from({ length: total }, (_, i) => (
        <Star key={i} size={size} weight={i < stars ? "fill" : "regular"}
              className={i < stars ? "text-amber-400" : "text-zinc-700"} />
      ))}
    </span>
  );
}

export function GBar({ value, color = "#E31E24", h = 8, testid }) {
  return (
    <div className="w-full bg-zinc-800/80 rounded-full overflow-hidden" style={{ height: h }}>
      <div className="h-full rounded-full gami-bar" data-testid={testid}
           style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: `linear-gradient(90deg, ${color}aa, ${color})` }} />
    </div>
  );
}

export function KPI({ label, value, sub, icon: Icon, color = "#E31E24", testid, delay = 0 }) {
  return (
    <div className="gami-card gami-rise p-5" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">{label}</p>
        {Icon && <Icon size={18} weight="fill" style={{ color }} />}
      </div>
      <p className="text-3xl font-black tabular-nums mt-2 text-white">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

export function PeriodTabs({ value, onChange, options = PERIODS, testid = "period" }) {
  return (
    <div className="inline-flex rounded-full bg-zinc-900 border border-zinc-800 p-1 text-xs">
      {options.map(([k, l]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
                className={`px-3.5 py-1.5 rounded-full font-bold uppercase tracking-wider transition-colors ${value === k ? "bg-[var(--brand,#E31E24)] text-white" : "text-zinc-400 hover:text-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export const MEDAL = { 1: "#F5B83D", 2: "#C0C8D4", 3: "#C97B48" };

export function RankMedal({ rank, size = 30 }) {
  const c = MEDAL[rank];
  if (!c) return <span className="w-[30px] text-center font-black text-zinc-500 tabular-nums">{rank}</span>;
  return (
    <span className="grid place-items-center rounded-full font-black text-zinc-950 text-xs shrink-0"
          style={{ width: size, height: size, background: c, boxShadow: `0 0 14px ${c}88` }}>{rank}</span>
  );
}

export const Empty = ({ children }) => (
  <div className="gami-card p-10 text-center text-zinc-500 text-sm">{children}</div>
);

export const Lightn = Lightning;
